/**
* Data model that represents an appointment (FlexAppointment)
*
* @class LocationModel
*
* @param {object} data
* @param {Function} getQueuesByLocationId function uses to look up Queues for the Location
*/
var LocationModel = function (data, getQueuesByLocationId) {
  var self;	// Convenience reference so the context of 'this' becomes clearer

  // Predicate this data model on the AbstractDataModel.
  // This establishes core functionality required of DataModels,
  // which can be overwritten by methods below.
  // Specifically, conversion of XML data to JSON
  AbstractDataModel.apply(this, arguments);

  self = this;

  // Observable variables represented by this data model
  self.allowResourceSelection = false;
  self.askForSMSOptIn = null;
  self.defaultPhoneCountryCode = null;
  self.description = null;
  self.featureToggles = {};
  self.id = null;
  self.languages = ko.observableArray([]);
  self.name = null;
  self.merchantId = null;
  self.showTransactionTypes = null;
  self.timeZone = null;
  self.usePartySize = null;

  self.appointmentCustomScreensWS = new AppointmentCustomScreensWS(window.qcal);

  self.customFields = ko.computed(function () {
    return self.appointmentCustomScreensWS.fields();
  });

  /**
   * Returns a list of custom screens
   * Filters out duplicated field names and only returns
   * screens that have at least one field
   *
   * @method customScreens
   * @return {Array}
   */
  self.customScreens = ko.computed(function () {
    var ttId = qcal.addAppointmentTicket.transactionTypeId();
    // Read all the custom screens from the sub-service
    var screens = self.appointmentCustomScreensWS.screens().reduce(function (arr, screen) {
      if (!screen.transactionTypeId || Number(screen.transactionTypeId) === Number(ttId)) {
        arr.push(screen);
      }
      return arr
    }, []);

    // Filter screens, returning only those which have at least
    // one field, after filtering out for unique fields
    return screens.reduce(function (arr, scrn, scrnIdx) {
      // Make sure the screen has fields
      if (scrn.fields().length > 0) {
        // Filter out fields that are not unique
        var fields = scrn.fields().reduce(function (fldArr, fld) {
          if (self.customScreenFieldIsUnique(fld, scrnIdx, screens)) {
            fldArr.push(fld);
          }
          return fldArr;
        }, []);
      }
      // If the screen still has fields after filtering, then the screen
      // can be displayed
      if (fields.length > 0) {
        arr.push(scrn);
      }
      // Return the filtered screens
      return arr;
    }, []);
  });


	/**
	* Watch the custom fields and create an array of observables for each field
	*
	* @method customFields.subscribe
	* @return none
	*/
  self.customFields.subscribe(function () {
    // Pack the custom fields as values in the addAppointmentTicket
    var aTemp = self.customFields().reduce(function (arr, field) {
      arr[field.name] = ko.observable();

      return arr;
    }, []);

    qcal.addAppointmentTicket.customProps(aTemp);
  });

  /**
   * Ensures that a custom screen field is only rendered to the
   * add appointment interface once
   *
   * @method customScreenFieldIsUnique
   * @param {Object} field
   * @param {Number} screenIdx
   * @return {Boolean}
   */
  self.customScreenFieldIsUnique = function (field, screenIdx, screens) {
    // If there are screens to iterate over, do this
    if (screens.length > 0) {
      // Get the fields that match the field value passed as a param by
      // looping over each screen, examining it's fields array, and returning
      // all fields that match the name of the field passed as the parameter
      var matchingFields = screens.reduce(function (arr, scrn, idx) {
        // If the screen had fields, iterate over each field, looking for
        // one with a matching name
        if (scrn.fields().length > 0) {
          // Filter each field, only returning the ones which match the name
          // of the field passed as the param
          arr = arr.concat(scrn.fields().filter(function (fld) {
            // If the screen is no greater than the field's screen
            // (this prevents fields on earlier screens matching against
            // fields that would come after them; the ones that come after
            // are the duplicates, not the earlier instances)
            // and the field is not the iterated field, and they have the
            // same name, return that one
            return idx <= screenIdx && fld !== field && fld.name == field.name;
          }));
        }
        // return the array of fields from each screen
        return arr;
      }, []);

      // If there were no matching fields, then the passed field is unique
      return matchingFields.length == 0
    }

    // If there are no screens to iterate over, then whatever was
    // passed must be unique.
    return true;
  };


  /**
   * Convenience function to check the feature toggle value
   *
   * @method emailRequired
   * @returns {Boolean}
   */
  self.emailRequired = function () {
    return self.featureToggles.emailRequired === true;
  };


  /**
   * Retrieves message bundle for the location id
   *
   * @method getMessageBundle
   * @returns none
   */
  self.getMessageBundle = function () {
    $.ajax({
      url: '/qless/api/v1/messages/calendar/location/' + self.id,
      method: 'GET',
      success: self.parseFeatureToggles
    });
  };


  /**
   * Parses a message bundle for Feature Toggles
   *
   * @method parseFeatureToggles
   * @param {XMLDocument} data
   * @returns none
   */
  self.parseFeatureToggles = function (data) {
    var bndl = null;

    if (data) {
      var json = xmlToJson.parse(data);

      if (json && json.bundles) {
        bndl = Array.isArray(json.bundles.bundle) ? json.bundles.bundle[0] : json.bundles.bundle;

        bndl.m.forEach(function (m) {
          if (m.n.indexOf('FT') == 0) {
            self.featureToggles[m.n.replace(/^FT/, '')] = m.text;
          }
        });
      }
    }
  };


	/**
	* Returns a collection of QueueModels which belong to this location
	*
	* @method queues
	* @return {Array}
	*/
  self.queues = ko.computed(function () {
    return getQueuesByLocationId(this.id);
  }, self, { deferEvaluation: true });


	/**
	* Returns a collection of ResourceModels which belong to this location
	*
	* @method resources
	* @return {Array}
	*/
  self.resources = ko.computed(function () {
    return qcal.resourcesWS.getResourcesByLocationId(self.id);
  });


	/**
	* Returns a collection of Resource IDs belonging to this location
	*
	* @method resourceIds
	* @return {Array}
	*/
  self.resourceIds = ko.computed(function () {
    var aResources = self.resources();

    return aResources.reduce(function (arr, r) {
      arr.push(r.id.toString());

      return arr;
    }, []);
  });


	/**
	* Returns a collection of resources selected for this location
	*
	* @method availableResources
	* @return {Array}
	*/
  self.availableResources = ko.computed(function () {
    var aResources = self.resources();
    var selectedResources = qcal.selectedResources();

    return aResources.filter(function (r) {
      return (selectedResources.indexOf(r.id.toString()) > -1);
    });
  });


	/**
	* Returns a collection of language names available at this location
	*
	* @method languageNames
	* @return {Array}
	*/
  self.languageNames = ko.computed(function () {
    var bundle = qcal.messagesWS.bundle();	// This dependency allows this function to
    // auto-update when the MessagesWS recieves data

    if (bundle) {
      return self.languages().reduce(function (arr, lang) {
        arr.push(qcal.messagesWS.languageName(self.languages()[i]));
        return arr;
      }, []);
    }

    return [];
  });


	/**
	* Method to convert the XML data representing this DataModel
	* into observed variables above.
	*
	* @method loadData
	* @param {XMLString} data XML String representing this model's data
	* @return {LocationModel} Returns the data model as chainable, for best practice
	*/
  self.loadData = function (data) {
    var bAllowResourceSelection = null,
      langs = [];


    // Call the superclass method of the same name
    // This loads the XML into the object's data member
    // and converts the data into JSON for easy assignment
    AbstractDataModel.prototype.loadData.call(self, data);

    self.askForSMSOptIn = self.parseBoolean(self.json.askForSMSOptIn);
    self.defaultPhoneCountryCode = self.json.defaultPhoneCountryCode || self.json.location.defaultPhoneCountryCode || 1;
    self.description = self.json.description;
    self.id = parseInt(self.json.id, 0);
    self.merchantId = parseInt(self.json.merchantId);
    self.name = self.json.name;
    self.showTransactionTypes = (self.json.kioskInfo && self.json.kioskInfo.useTransactionTypes) ? self.parseBoolean(self.json.kioskInfo.useTransactionTypes) : null;
    self.timeZone = self.json.location.timeZone || self.json.location.address.timeZone;
    self.usePartySize = self.parseBoolean(self.json.usePartySize);

    langs = (Array.isArray(self.json.language)) ? self.json.language : (self.json.language != null) ? [self.json.language] : [];
    self.languages(langs);

    bAllowResourceSelection = self.json.kioskInfo && self.json.kioskInfo.allowResourceSelection;

    if (bAllowResourceSelection) {
      self.allowResourceSelection = (bAllowResourceSelection.toLowerCase() == 'true') ? true : false;
    }

    // Request the message bundle so feature toggles can be parsed
    self.getMessageBundle();

    // Return self (LocationModel) to be chainable, for best practice
    return self;
  };


	/**
	* Indicates whether this location has any queues that have appointments enabled
	*
	* @method appointmentsAvailable
	* @return {Boolean}
	*/
  self.appointmentsAvailable = ko.computed(function () {
    return (self.queues().length > 0);
  }, self, { deferEvaluation: true });


  // If the model was initialized with a data parameter,
  // call the loadData method to populate the observed variables.
  if (data) {
    self.loadData(data);
  }

  // Return self (loadData) to be chainable, for best practice
  return self;
};

LocationModel.prototype = AbstractDataModel.prototype;
LocationModel.prototype.constructor = LocationModel;
